import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  Users,
  FolderOpen,
  Package,
  ShoppingCart,
  FileText,
  Warehouse,
  PackageCheck,
  UserCog,
  Receipt,
  Clock,
  CalendarDays,
  Bell,
  AlarmClock,
  CalendarHeart,
  Shield,
  Wrench,
  Palette,
  LogOut,
  Boxes,
  ArrowLeftRight,
  Undo2,
  BookOpen,
  ClipboardPen,
  Wallet,
} from 'lucide-react';
import { Sidebar, SidebarUserCard, type SidebarNavGroup } from '@elka/layout';
import { useTheme } from '@elka/theme';
import { useIsSuperAdmin } from '@/features/auth/hooks/useIsAdmin';
import { useOpenPurchaseRequestCount } from '@/features/purchase-requests/hooks/usePurchaseRequests';
import { usePendingPettyCashCount } from '@/features/admin/hooks/useAdminPettyCash';

interface AppSidebarProps {
  user?: {
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
  };
  onLogout?: () => void;
}

const ICON = 18;

/**
 * Calm-v2 app shell sidebar for elka-manager. Maps the existing routes into the
 * redesign's four nav groups (work / clients+projects / procurement / team).
 * The KPI dashboard (/dashboard) is the intentionally chrome-less route — it is
 * linked out of the shell, not rendered inside it. No new pages are introduced.
 */
export function AppSidebar({ user, onLogout }: AppSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = useIsSuperAdmin();
  const openPurchaseRequests = useOpenPurchaseRequestCount();
  const pendingPettyCash = usePendingPettyCashCount();
  const { theme, toggleTheme } = useTheme();

  const groups: SidebarNavGroup[] = useMemo(() => {
    const g: SidebarNavGroup[] = [
      {
        title: 'עבודה יומית',
        items: [
          {
            key: 'dashboard',
            label: 'דשבורד',
            icon: <LayoutDashboard size={ICON} />,
            href: '/dashboard',
          },
          {
            key: 'sidur',
            label: 'סידור עבודה',
            icon: <Calendar size={ICON} />,
            href: '/projects/sidur',
          },
          {
            key: 'daily-reports',
            label: 'יומני עבודה',
            icon: <ClipboardList size={ICON} />,
            href: '/projects/daily-reports',
          },
        ],
      },
      {
        title: 'לקוחות ופרויקטים',
        items: [
          {
            key: 'clients',
            label: 'לקוחות',
            icon: <Users size={ICON} />,
            href: '/projects/clients',
          },
          {
            key: 'projects',
            label: 'פרויקטים',
            icon: <FolderOpen size={ICON} />,
            href: '/projects/list',
          },
        ],
      },
      {
        title: 'רכש וספקים',
        items: [
          {
            key: 'suppliers',
            label: 'ספקים',
            icon: <Package size={ICON} />,
            href: '/rechesh/suppliers',
          },
          {
            key: 'orders',
            label: 'הזמנות רכש',
            icon: <ShoppingCart size={ICON} />,
            href: '/rechesh/orders',
          },
          {
            key: 'supplier-bids',
            label: 'הצעות מחיר רכש',
            icon: <FileText size={ICON} />,
            href: '/rechesh/supplier-bids',
          },
          {
            key: 'goods-receiving',
            label: 'קבלת סחורה',
            icon: <PackageCheck size={ICON} />,
            href: '/rechesh/goods-receiving',
          },
          {
            key: 'purchase-requests',
            label: 'בקשות רכש',
            icon: <ClipboardPen size={ICON} />,
            href: '/rechesh/purchase-requests',
            ...(openPurchaseRequests > 0 && { badge: openPurchaseRequests }),
          },
        ],
      },
      {
        title: 'מחסן ומלאי',
        items: [
          {
            key: 'inventory',
            label: 'מלאי',
            icon: <Boxes size={ICON} />,
            href: '/rechesh/inventory',
          },
          {
            key: 'warehouse-incoming',
            label: 'קליטה למחסן',
            icon: <Warehouse size={ICON} />,
            href: '/rechesh/warehouse',
          },
          {
            key: 'inventory-movements',
            label: 'איסופים והחזרות',
            icon: <ArrowLeftRight size={ICON} />,
            href: '/rechesh/movements',
          },
          {
            key: 'warehouse-returns',
            label: 'אישור החזרות',
            icon: <Undo2 size={ICON} />,
            href: '/rechesh/warehouse/returns',
          },
        ],
      },
      {
        title: 'צוות וניהול',
        items: [
          {
            key: 'workers',
            label: 'עובדים',
            icon: <UserCog size={ICON} />,
            href: '/admin/workers',
          },
          ...(isSuperAdmin
            ? [
                {
                  key: 'payslips',
                  label: 'תלושים',
                  icon: <Receipt size={ICON} />,
                  href: '/admin/payslips',
                },
              ]
            : []),
          {
            key: 'time-reports',
            label: 'שעות',
            icon: <Clock size={ICON} />,
            href: '/admin/time-reports',
          },
          {
            key: 'vacation-requests',
            label: 'חופשה',
            icon: <CalendarDays size={ICON} />,
            href: '/admin/vacation-requests',
          },
          {
            key: 'petty-cash',
            label: 'קופה קטנה',
            icon: <Wallet size={ICON} />,
            href: '/admin/petty-cash',
            ...(pendingPettyCash > 0 && { badge: pendingPettyCash }),
          },
          {
            key: 'notifications',
            label: 'הודעות',
            icon: <Bell size={ICON} />,
            href: '/admin/notifications',
          },
          {
            key: 'reminder-settings',
            label: 'תזכורות',
            icon: <AlarmClock size={ICON} />,
            href: '/admin/reminder-settings',
          },
          {
            key: 'holidays',
            label: 'חגים',
            icon: <CalendarHeart size={ICON} />,
            href: '/admin/holidays',
          },
          {
            key: 'toolbox',
            label: 'ארגז כלים',
            icon: <Wrench size={ICON} />,
            href: '/projects/toolbox',
          },
          ...(isSuperAdmin
            ? [
                {
                  key: 'audit-logs',
                  label: 'ביקורת',
                  icon: <Shield size={ICON} />,
                  href: '/admin/audit-logs',
                },
              ]
            : []),
        ],
      },
    ];
    return g;
  }, [isSuperAdmin, openPurchaseRequests, pendingPettyCash]);

  const isWikiActive = location.pathname.startsWith('/wiki');

  // Derive the active key from the current path: match the nav item whose href
  // is the longest prefix of the current pathname.
  const activeKey = useMemo(() => {
    const path = location.pathname;
    let best: { key: string; len: number } | null = null;
    for (const group of groups) {
      for (const item of group.items) {
        if (item.href && path.startsWith(item.href) && (!best || item.href.length > best.len)) {
          best = { key: item.key, len: item.href.length };
        }
      }
    }
    return best?.key;
  }, [groups, location.pathname]);

  const displayName = user?.displayName || user?.email || 'משתמש';

  return (
    <Sidebar
      appName="אלקה מערכות ניהול"
      tagline="חשמלאות תעשייתית"
      width={240}
      groups={groups}
      activeKey={activeKey}
      onNavigate={(item) => item.href && navigate(item.href)}
      footer={
        <div className="flex w-full flex-col gap-1.5">
          <a
            href="/wiki"
            onClick={(e) => {
              e.preventDefault();
              navigate('/wiki');
            }}
            className={`rounded-control flex cursor-pointer items-center gap-2.5 px-2.5 py-2 text-[13px] transition-colors duration-150 ${
              isWikiActive
                ? 'bg-brand-soft text-brand font-bold'
                : 'text-ink-2 hover:bg-surface-soft font-medium'
            }`}
          >
            <span
              className={`flex w-[18px] shrink-0 items-center justify-center ${
                isWikiActive ? 'text-brand' : 'text-muted'
              }`}
            >
              <BookOpen size={ICON} />
            </span>
            <span className="min-w-0 flex-1 truncate">מדריך</span>
          </a>
          <SidebarUserCard
            name={displayName}
            role="מנהל"
            photoURL={user?.photoURL}
            actions={[
              {
                label: theme === 'elkaBlue' ? 'ערכת זהב' : 'ערכת כחול',
                icon: Palette,
                onClick: toggleTheme,
              },
              ...(onLogout
                ? [{ label: 'התנתק', icon: LogOut, onClick: onLogout, danger: true }]
                : []),
            ]}
          />
        </div>
      }
    />
  );
}
